import { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import TasksByTagProgressPanel from './TasksByTagProgressPanel';

/**
 * Заметки преподавателей + журнал задач по темам.
 * studentId — чей прогресс; canEdit — учитель редактирует свою заметку;
 * showJournal — вторая внутренняя вкладка (у родителя обычно false);
 * onSelectTaskSlug — клик по задаче из журнала тегов (карточка ученика).
 */
export default function StudentTopicProgressPanel({
  studentId,
  canEdit = false,
  showJournal = true,
  onSelectTaskSlug,
}) {
  const [mainTab, setMainTab] = useState('notes'); // 'notes' | 'journal'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);

  const targetStudentId = Number(studentId);

  const loadNotes = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!targetStudentId || Number.isNaN(targetStudentId)) return;
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await api.get(`/teacher/students/${targetStudentId}/topic-progress-notes/`);
      const list = Array.isArray(res.data) ? res.data : [];
      setItems(list);
      const mine = list.find((row) => row.is_me);
      if (mine) setEditNotes(mine.notes ?? '');
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message || 'Не удалось загрузить заметки.';
      setError(typeof msg === 'string' ? msg : 'Не удалось загрузить заметки.');
      setItems([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [targetStudentId]);

  useEffect(() => {
    if (targetStudentId && !Number.isNaN(targetStudentId)) {
      loadNotes();
    }
  }, [targetStudentId, loadNotes]);

  useEffect(() => {
    if (!saveOk) return;
    const t = setTimeout(() => setSaveOk(false), 2500);
    return () => clearTimeout(t);
  }, [saveOk]);

  async function handleSaveNotes() {
    if (!canEdit || !targetStudentId) return;
    setSaving(true);
    setSaveError('');
    try {
      await api.patch(`/teacher/students/${targetStudentId}/topic-progress-notes/`, {
        notes: editNotes,
      });
      setSaveOk(true);
      await loadNotes({ silent: true });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось сохранить.';
      setSaveError(typeof msg === 'string' ? msg : 'Не удалось сохранить.');
    } finally {
      setSaving(false);
    }
  }

  if (loading && !error) {
    return (
      <div className="topic-progress-panel-loading text-secondary">
        <p>Загрузка…</p>
      </div>
    );
  }

  const effectiveTab = showJournal ? mainTab : 'notes';

  return (
    <div className="student-topic-progress-panel">
      {showJournal && (
        <div className="topic-progress-main-tabs" role="tablist" aria-label="Вид прогресса">
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === 'notes'}
            className={`btn btn-sm ${effectiveTab === 'notes' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMainTab('notes')}
          >
            Заметки по темам
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === 'journal'}
            className={`btn btn-sm ${effectiveTab === 'journal' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMainTab('journal')}
          >
            Журнал задач по темам
          </button>
        </div>
      )}

      {effectiveTab === 'journal' && showJournal && (
        <div className="topic-progress-stub-card" role="tabpanel">
          <TasksByTagProgressPanel
            studentId={targetStudentId}
            onSelectTaskSlug={onSelectTaskSlug}
          />
        </div>
      )}

      {effectiveTab === 'notes' && (
        <div className="topic-progress-notes-panel" role="tabpanel">
          {error && <div className="alert alert-error">{error}</div>}
          {saveError && <div className="alert alert-error">{saveError}</div>}
          {saveOk && <div className="alert alert-success">Сохранено.</div>}

          {!error && items.length === 0 && !loading && (
            <p className="text-secondary">
              Нет активных связей с преподавателями — пока нечего показать.
            </p>
          )}

          {items.map((item) => {
            const updatedLabel = item.updated_at
              ? new Date(item.updated_at).toLocaleString('ru-RU')
              : 'ещё не обновлялось';

            const inner = (
              <div className="progress-note-card-inner">
                <div className="progress-note-meta text-secondary">
                  Обновлено: {updatedLabel}
                </div>
                {canEdit && item.is_me ? (
                  <div className="form-group">
                    <label htmlFor={`notes-edit-${item.teacher_id}`}>Текст заметки</label>
                    <textarea
                      id={`notes-edit-${item.teacher_id}`}
                      className="form-control"
                      rows={8}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      disabled={saving}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={saving}
                      onClick={handleSaveNotes}
                    >
                      {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                  </div>
                ) : (
                  <pre className="progress-note-readonly">{item.notes || '—'}</pre>
                )}
              </div>
            );

            if (canEdit && !item.is_me) {
              return (
                <details key={item.teacher_id} className="progress-note-card">
                  <summary className="progress-note-summary">{item.teacher_display_name}</summary>
                  {inner}
                </details>
              );
            }

            return (
              <section key={item.teacher_id} className="progress-note-card progress-note-card-expanded">
                <h2 className="progress-note-title">{item.teacher_display_name}</h2>
                {inner}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
